import type {
  ReferralFiltersPayload,
  ReferralFilters,
  ReferralPrograms,
} from 'src/types/referral-v2';
import type {AgencyList} from 'src/types/referrer-portal';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useDispatch, useSelector} from 'react-redux';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import {isReferrerPortal} from 'src/components/referral-v2/common';
import {getAllReferralsPrograms} from 'src/action-creators/referrer-portal/tracking';
import {
  Button,
  Dropdown,
} from '@spaced-out/ui-design-system/lib/components/index';
import ReferralFilter from 'src/components/referral-v2/referral-filter/referral-filter';
import css from 'src/components/referral-v2/tracking/tracking.css';


type FilterProps = {
  from: string;
  handleApplyFilter: (filters: ReferralFiltersPayload[]) => unknown;
  searchText: string;
};

export const Filter = ({from, handleApplyFilter}: FilterProps) => {
  const [isOpen, setOpenPanel] = React.useState(false);
  const [isFilterLoading, setIsFilterLoading] = React.useState(false);
  const [trackingFilters, setTrackingFilters] = React.useState<
    ReferralFilters[]
  >([]);

  const dispatch = useDispatch();

  React.useEffect(() => {
    if (isOpen && from === 'tracking') {
      setIsFilterLoading(true);
      dispatch(reduxApi.get('tracking/filters')).then(
        (response: ReferralFilters[]) => {
          if (response) {
            setIsFilterLoading(false);
            setTrackingFilters(response);
          }
        },
      );
    }
  }, [from, isOpen]);

  const filters = from === 'tracking' ? trackingFilters : [];

  return (
    <>
      <Button
        ariaLabel="Icon Button"
        iconRightName="bars-filter"
        onClick={() => setOpenPanel(!isOpen)}
        size="medium"
        type="ghost"
      />
      <ReferralFilter
        filters={filters || []}
        selectedTab={''}
        handleApplyFilter={handleApplyFilter}
        loading={isFilterLoading}
        isOpen={isOpen}
        onClose={() => setOpenPanel(!isOpen)}
      />
    </>
  );
};

export const AgencyAndProgramFilter = ({
  agencyId,
  programId,
  onChangeFilter,
  required = false,
}: {
  agencyId: string | null | undefined;
  programId: string | null | undefined;
  onChangeFilter: (
    filterName: string,
    value?: string | null | undefined,
  ) => unknown;
  required?: boolean;
}) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const agencyList: AgencyList[] | null | undefined = useSelector(
    (state) => state.referrerPortal.agency_list,
  );
  const programList: ReferralPrograms[] | null | undefined = useSelector(
    (state) => state.referrerPortal.referral_programs,
  );

  const agencyFilterOptions = agencyList?.map((agency: AgencyList) => ({
    key: agency.id.toString(),
    label: agency.name,
  }));

  const programFilterOptions = programList?.map(
    (program: ReferralPrograms) => ({
      key: program.id.toString(),
      label: program.name,
    }),
  );

  const selectedProgramName = programList?.find(
    (program: ReferralPrograms) => program.id.toString() === programId,
  )?.name;

  const selectedAgencyName = agencyList?.find(
    (agency: AgencyList) => agency.id === agencyId,
  )?.name;

  const isReferrerPortalEnv = isReferrerPortal();

  const isProgramLoading = useAsyncDependency(() => {
    if (agencyId) {
      dispatch(getAllReferralsPrograms(agencyId));
    }
    return Promise.all([]);
  }, [agencyId]);

  const handleCLearFilter = () => {
    if (isReferrerPortalEnv) {
      onChangeFilter('agency_id', null);
    }
    onChangeFilter('program_id', null);
  };

  React.useEffect(() => {
    const defaultProgram = programList?.find(
      (program: ReferralPrograms) => program.is_default_program,
    );

    if (defaultProgram && agencyId && required) {
      onChangeFilter('program_id', defaultProgram.id.toString());
    }
  }, [programList]);

  const showClearButton =
    (isReferrerPortalEnv ? agencyId : programId) && !required;

  return (
    <div className={css.defaultGap}>
      {isReferrerPortalEnv && (
        <Dropdown
          dropdownInputText={
            selectedAgencyName ??
            t('Select_Organization', 'Select Organization')
          }
          onChange={(option) => {
            onChangeFilter('agency_id', option.key);
          }}
          menu={{
            options: agencyFilterOptions,
            selectedKeys: agencyId ? [agencyId.toString()] : [],
            staticLabels: {
              SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
              RESULTS: `${t('results', 'results')}`,
              RESULT: `${t('result', 'result')}`,
            },
          }}
        />
      )}
      <Dropdown
        disabled={!agencyId || isProgramLoading}
        dropdownInputText={
          selectedProgramName ?? t('Select_Program', 'Select Program')
        }
        onChange={(option) => onChangeFilter('program_id', option.key)}
        menu={{
          options: programFilterOptions,
          selectedKeys: programId ? [programId.toString()] : [],
          allowSearch: true,
          staticLabels: {
            SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
            RESULTS: `${t('results', 'results')}`,
            RESULT: `${t('result', 'result')}`,
          },
        }}
      />
      {showClearButton && (
        <Button type="ghost" onClick={handleCLearFilter}>
          {t('Clear', 'Clear')}
        </Button>
      )}
    </div>
  );
};
